import { useEffect, useState } from "react";
import { addFxLink, listFxLinks, removeFxLink, type FxLink } from "./api";
import { listDocuments, loadCompanies, readClient, searchClients, type FxClient, type FxCompany, type FxDoc } from "./facturx";
import { Modal } from "./Modal";
import { PieceSheet } from "./PieceSheet";
import type { CrmFiche } from "./types";
import { aimedFind, clearFind } from "./find";

const KINDS = [
  { id: "quote", label: "Devis" },
  { id: "invoice", label: "Factures" },
  { id: "credit_note", label: "Avoirs" },
] as const;

export function FactureBoard({ fiche }: { fiche: CrmFiche }) {
  const [docs, setDocs] = useState<FxDoc[]>([]);
  const [error, setError] = useState("");
  const [kind, setKind] = useState<(typeof KINDS)[number]["id"]>("invoice");
  const [draft, setDraft] = useState<"quote" | "invoice" | null>(null);
  const [tick, setTick] = useState(0);
  const [client, setClient] = useState<FxClient | null>(null);
  const [links, setLinks] = useState<FxLink[]>([{ company_id: fiche.facturx_company_id, client_id: fiche.facturx_client_id }]);
  const [names, setNames] = useState<Record<number, string>>({});
  const [activeId, setActiveId] = useState(fiche.facturx_client_id);
  const [adding, setAdding] = useState(false);
  const [mark, setMark] = useState(0);
  const active = links.find((row) => row.client_id === activeId) || links[0];

  useEffect(() => {
    let alive = true;
    void listFxLinks(fiche.id)
      .then((rows) => {
        if (!alive) return;
        const next = rows.length > 0 ? rows : [{ company_id: fiche.facturx_company_id, client_id: fiche.facturx_client_id }];
        setLinks(next);
        setActiveId((current) => (next.some((row) => row.client_id === current) ? current : next[0].client_id));
      })
      .catch(() => undefined);
    return () => { alive = false; };
  }, [fiche.id, fiche.facturx_company_id, fiche.facturx_client_id, tick]);

  useEffect(() => {
    let alive = true;
    void Promise.all(links.filter((row) => row.client_id > 0).map(async (row) => {
      const data = await readClient(row.client_id);
      return [row.client_id, data.client.name] as const;
    })).then((pairs) => {
      if (!alive) return;
      const next: Record<number, string> = {};
      pairs.forEach(([id, name]) => { next[id] = name; });
      setNames(next);
    }).catch(() => undefined);
    return () => { alive = false; };
  }, [links]);

  useEffect(() => {
    let alive = true;
    setError("");
    if (!active || active.client_id <= 0) return;
    void listDocuments(active.company_id, active.client_id)
      .then((data) => {
        if (alive) setDocs(data.documents || []);
      })
      .catch((reason: unknown) => {
        if (alive) setError(reason instanceof Error ? reason.message : "Factures ne répond pas.");
      });
    return () => {
      alive = false;
    };
  }, [active, tick]);

  useEffect(() => {
    let alive = true;
    if (!active || active.client_id <= 0) return;
    void readClient(active.client_id)
      .then((data) => {
        if (alive) setClient(data.client);
      })
      .catch(() => {
        if (alive) setClient(null);
      });
    return () => {
      alive = false;
    };
  }, [active]);

  useEffect(() => {
    const open = () => {
      const hit = aimedFind("Devis", fiche.id) || aimedFind("Facture", fiche.id);
      if (!hit) return;
      setKind(hit.kind === "Devis" ? "quote" : "invoice");
      setActiveId(fiche.facturx_client_id);
      setMark(hit.docId);
      clearFind();
      window.setTimeout(() => document.getElementById("crm-factures")?.scrollIntoView({ block: "nearest" }), 80);
    };
    open();
    window.addEventListener("crm-find", open);
    return () => window.removeEventListener("crm-find", open);
  }, [fiche.id, fiche.facturx_client_id]);

  const own = Boolean(client && active && client.id !== fiche.facturx_client_id);
  const place = [own ? client?.address_line : (client?.address_line || fiche.address), own ? client?.postal_code : (client?.postal_code || fiche.postal_code), own ? client?.city : (client?.city || fiche.city)].filter(Boolean).join(", ");
  const person = client?.contacts?.[0];
  const contact = [person?.first_name, person?.last_name].map((part) => (part || "").trim()).filter(Boolean).join(" ");
  const email = (client?.email || person?.email || (own ? "" : fiche.email) || "").trim();
  const phone = (client?.phone || person?.phone || (own ? "" : fiche.phone) || "").trim();
  const siret = (client?.siret || "").replace(/\D/g, "");
  const rows = docs.filter((doc) => doc.doc_type === kind);
  const current = KINDS.find((item) => item.id === kind);

  const invoices = docs.filter((doc) => doc.doc_type === "invoice" && doc.status === "validated");
  const billed = sum(invoices, "total_ttc_cents");
  const paid = sum(invoices, "paid_cents");
  const due = sum(invoices, "remaining_cents");

  return (
    <div id="crm-factures" className="grid gap-3">
      <div className="flex flex-wrap items-center gap-1.5">
        {links.filter((row) => row.client_id > 0).map((row) => {
          const on = row.client_id === active?.client_id;
          const main = row.client_id === fiche.facturx_client_id;
          return (
            <span key={row.client_id} className={on ? "crm-soft inline-flex items-center gap-1 rounded-full py-1 pl-3 pr-1 text-sm font-semibold" : "inline-flex items-center gap-1 rounded-full py-1 pl-3 pr-1 text-sm font-medium text-slate-500 hover:bg-black/5"}>
              <button type="button" className="max-w-[12rem] truncate" onClick={() => setActiveId(row.client_id)}>{names[row.client_id] || `Client ${row.client_id}`}</button>
              {main ? null : (
                <button type="button" className="grid h-5 w-5 place-items-center rounded-full text-xs opacity-60 hover:opacity-100" aria-label="Retirer ce client" title="Retirer" onClick={() => void removeFxLink(fiche.id, row.client_id).then((rows) => { setLinks(rows); setActiveId(fiche.facturx_client_id); }).catch((reason: unknown) => setError(reason instanceof Error ? reason.message : "Retrait impossible"))}>×</button>
              )}
            </span>
          );
        })}
        <button type="button" className="rounded-full border border-slate-200 px-3 py-1 text-sm font-semibold" onClick={() => setAdding(true)}>+ Client</button>
      </div>
    <div className="grid items-stretch gap-4 xl:grid-cols-[minmax(0,1fr)_1px_minmax(0,1fr)_1px_minmax(32rem,40rem)] xl:gap-x-5">
      <div className="crm-soft rounded-2xl px-4 py-3">
        <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">Client</p>
        <p className="mt-1 flex items-center gap-1.5 font-semibold">
          <span className="min-w-0 truncate">{client?.name || fiche.name}</span>
          <CopyMark text={client?.name || fiche.name} label="Copier la société" />
        </p>
        {place ? (
          <p className="mt-1 flex items-start gap-1.5 text-sm text-slate-500">
            <span className="min-w-0">{place}</span>
            <CopyMark text={place} label="Copier l’adresse" />
          </p>
        ) : null}
        {siret ? (
          <p className="mt-1 flex items-center gap-1.5 text-sm text-slate-500">
            <span>SIRET {formatSiret(siret)}</span>
            <CopyMark text={siret} label="Copier le SIRET" />
          </p>
        ) : null}
        {contact ? (
          <p className="mt-1 flex items-center gap-1.5 text-sm text-slate-500">
            <span className="min-w-0 truncate">{contact}</span>
            <CopyMark text={contact} label="Copier le contact" />
          </p>
        ) : null}
        {email ? (
          <p className="flex items-center gap-1.5 text-sm text-slate-500">
            <span className="min-w-0 truncate">{email}</span>
            <CopyMark text={email} label="Copier l’e-mail" />
          </p>
        ) : null}
        {phone ? (
          <p className="flex items-center gap-1.5 text-sm text-slate-500">
            <span>{formatPhone(phone)}</span>
            <CopyMark text={phone.replace(/\D/g, "")} label="Copier le téléphone" />
          </p>
        ) : null}
      </div>
      <div className="crm-split hidden xl:block" aria-hidden />
      <div className="min-w-0">
        <div className="flex flex-wrap gap-2">
          {KINDS.map((item) => {
            const count = docs.filter((doc) => doc.doc_type === item.id).length;
            const on = item.id === kind;
            return (
              <button key={item.id} type="button" className={on ? "crm-soft rounded-full px-3 py-1.5 text-sm font-semibold" : "rounded-full px-3 py-1.5 text-sm font-medium text-slate-500 hover:bg-black/5"} onClick={() => setKind(item.id)}>
                {item.label}
                <span className="ml-1.5 text-xs opacity-70">{count}</span>
              </button>
            );
          })}
        </div>
        {error ? <p className="mt-3 text-sm text-rose-700">{error}</p> : null}
        {rows.length === 0 && !error ? <p className="mt-4 text-sm text-slate-500">Aucun {current?.label.toLowerCase()}.</p> : null}
        <ul className="crm-scroll mt-3 grid max-h-[13.75rem] gap-2 overflow-y-auto pr-1">
          {rows.map((doc) => (
            <li key={doc.id} className={mark === doc.id ? "crm-edge rounded-2xl px-3 py-2.5 ring-2 ring-slate-400" : "crm-edge rounded-2xl px-3 py-2.5"}>
              <div className="flex items-baseline justify-between gap-2">
                <span className="truncate font-medium">{doc.number || "Sans numéro"}</span>
                <span className="shrink-0 text-sm font-semibold">{euros(doc.total_ttc_cents)}</span>
              </div>
              <p className="mt-1 text-xs text-slate-500">
                {doc.issue_date || "Sans date"} · {doc.status === "validated" ? "Validé" : "Brouillon"}
              </p>
            </li>
          ))}
        </ul>
      </div>
      <div className="crm-split hidden xl:block" aria-hidden />
      <aside className="grid content-start gap-2">
        <div className="grid grid-cols-3 gap-2">
          <Balance label="Facturé" value={euros(billed)} />
          <Balance label="Payé" value={euros(paid)} tone="text-emerald-600" />
          <Balance label="Solde" value={euros(due)} tone="text-orange-500" />
        </div>
        <p className="text-center text-xs text-slate-400">Factures validées seulement. Les devis et les brouillons ne comptent pas. Un clic crée un devis ou une facture en brouillon, déjà dans Factures.</p>
        <div className="mt-1 flex flex-wrap justify-center gap-2">
          <button type="button" className="crm-pill inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-sm font-semibold" onClick={() => setDraft("quote")}>
            <PlusIcon />
            Devis brouillon
          </button>
          <button type="button" className="crm-pill inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-sm font-semibold" onClick={() => setDraft("invoice")}>
            <PlusIcon />
            Facture brouillon
          </button>
        </div>
      </aside>
      {draft ? (
        <PieceSheet
          companyId={active?.company_id || fiche.facturx_company_id}
          clientId={active?.client_id || fiche.facturx_client_id}
          docType={draft}
          onClose={() => setDraft(null)}
          onCreated={() => {
            setKind(draft);
            setTick((value) => value + 1);
          }}
        />
      ) : null}
    </div>
    {adding ? (
      <AddFx
        ficheId={fiche.id}
        companyId={fiche.facturx_company_id}
        taken={links.map((row) => row.client_id)}
        onClose={() => setAdding(false)}
        onAdded={(rows, clientId) => { setLinks(rows); setActiveId(clientId); }}
      />
    ) : null}
    </div>
  );
}

function AddFx({ ficheId, companyId, taken, onClose, onAdded }: { ficheId: number; companyId: number; taken: number[]; onClose: () => void; onAdded: (rows: FxLink[], clientId: number) => void }) {
  const [companies, setCompanies] = useState<FxCompany[]>([]);
  const [company, setCompany] = useState(companyId);
  const [query, setQuery] = useState("");
  const [hits, setHits] = useState<{ id: number; name: string }[]>([]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void loadCompanies().then((data) => {
      const rows = data.companies || [];
      setCompanies(rows);
      setCompany(companyId || Number(rows[0]?.id || 0));
    }).catch(() => undefined);
  }, [companyId]);

  async function search() {
    if (!company) return;
    setBusy(true);
    setError("");
    try {
      const data = await searchClients(company, query.trim());
      setHits(data.clients || []);
    } catch (reason: unknown) {
      setError(reason instanceof Error ? reason.message : "Recherche impossible");
    } finally {
      setBusy(false);
    }
  }

  async function pick(id: number) {
    setBusy(true);
    setError("");
    try {
      const rows = await addFxLink(ficheId, company, id);
      onAdded(rows, id);
      onClose();
    } catch (reason: unknown) {
      setError(reason instanceof Error ? reason.message : "Client non ajouté");
      setBusy(false);
    }
  }

  return (
    <Modal onClose={onClose} sheetClass="crm-pop w-full max-w-md rounded-2xl border border-slate-200 p-5 shadow-[0_30px_80px_-36px_rgba(15,23,42,0.45)]">
      {(close) => (
        <>
          <h2 className="text-lg font-semibold">Ajouter un client Factures</h2>
          <p className="mt-1 text-sm text-slate-500">Il s’ajoute à cette fiche. Le bloc continue sur le client choisi.</p>
          {companies.length > 1 ? (
            <select className="crm-field mt-4 w-full rounded-xl border px-3 py-2 text-sm" value={company} onChange={(event) => setCompany(Number(event.target.value))}>
              {companies.map((row) => <option key={row.id} value={row.id}>{row.legal_name || row.name || `Société ${row.id}`}</option>)}
            </select>
          ) : null}
          <div className="mt-3 flex gap-2">
            <input className="crm-field min-w-0 flex-1 rounded-xl border px-3 py-2 text-sm" value={query} placeholder="Nom, e-mail, SIRET" onChange={(event) => setQuery(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter") void search(); }} />
            <button type="button" className="rounded-full crm-fill px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-50" disabled={busy} onClick={() => void search()}>Chercher</button>
          </div>
          {error ? <p className="mt-2 text-sm text-rose-700">{error}</p> : null}
          <ul className="mt-3 max-h-56 space-y-1 overflow-auto">
            {hits.map((hit) => (
              <li key={hit.id}>
                <button type="button" className="w-full rounded-xl px-3 py-2 text-left text-sm hover:bg-black/5 disabled:opacity-40" disabled={busy || taken.includes(hit.id)} onClick={() => void pick(hit.id)}>
                  {hit.name}{taken.includes(hit.id) ? " · déjà lié" : ""}
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
    </Modal>
  );
}

function CopyMark({ text, label }: { text: string; label: string }) {
  const [ok, setOk] = useState(false);
  const value = text.trim();
  if (!value) return null;

  async function copy() {
    try {
      await navigator.clipboard.writeText(value);
    } catch {
      const el = document.createElement("textarea");
      el.value = value;
      el.setAttribute("readonly", "");
      el.style.position = "fixed";
      el.style.left = "-9999px";
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
    }
    setOk(true);
    window.setTimeout(() => setOk(false), 1200);
  }

  return (
    <button type="button" className="grid h-5 w-5 shrink-0 place-items-center rounded text-slate-400 hover:text-slate-600" title={ok ? "Copié" : label} aria-label={ok ? "Copié" : label} onClick={() => void copy()}>
      {ok ? (
        <svg viewBox="0 0 16 16" className="h-3.5 w-3.5" aria-hidden>
          <path d="M3.5 8.2 6.4 11 12.5 4.8" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      ) : (
        <svg viewBox="0 0 16 16" className="h-3.5 w-3.5" aria-hidden>
          <rect x="5.2" y="5.2" width="7.2" height="7.2" rx="1.2" fill="none" stroke="currentColor" strokeWidth="1.3" />
          <path d="M4.4 10.6H3.8A1.3 1.3 0 0 1 2.5 9.3V3.8A1.3 1.3 0 0 1 3.8 2.5h5.5A1.3 1.3 0 0 1 10.6 3.8v.6" fill="none" stroke="currentColor" strokeWidth="1.3" />
        </svg>
      )}
    </button>
  );
}

function formatSiret(digits: string): string {
  if (digits.length !== 14) return digits;
  return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6, 9)} ${digits.slice(9)}`;
}

function formatPhone(raw: string): string {
  const digits = raw.replace(/\D/g, "");
  if (digits.length !== 10) return raw;
  return digits.replace(/(\d{2})(?=\d)/g, "$1 ").trim();
}

function PlusIcon() {
  return (
    <svg viewBox="0 0 16 16" className="h-3.5 w-3.5" aria-hidden>
      <path d="M8 2.5v11M2.5 8h11" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

function Balance({ label, value, tone }: { label: string; value: string; tone?: string }) {
  return (
    <div className="crm-edge rounded-2xl px-3 py-2.5">
      <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">{label}</p>
      <p className={tone ? `mt-1 truncate text-base font-semibold ${tone}` : "mt-1 truncate text-base font-semibold"}>{value}</p>
    </div>
  );
}

function sum(rows: FxDoc[], key: "total_ttc_cents" | "paid_cents" | "remaining_cents"): number {
  return rows.reduce((total, row) => total + (Number(row[key]) || 0), 0);
}

function euros(cents: number | string | null): string {
  const value = Number(cents);
  if (!Number.isFinite(value)) return "";
  return (value / 100).toLocaleString("fr-FR", { style: "currency", currency: "EUR" });
}
